<template>
  <PageHeader>
    <template #header-title>
      {{ $t('Clients') }}
    </template>
    <template #header-description>
      <WdsStack justify-space-between>
        <WdsText>
          {{ $t('List of companies that have added you to their talent pool.') }}
        </WdsText>
      </WdsStack>
    </template>
    <template #header-actions>
      <WdsButton
        tiertary
        :href="laravelNamedRoute(namedRoutes.frontend.user.profile.edit)"
        class="d-flex align-items-center tw-gap-1"
      >
        Account settings
        <WdsIcon name="arrow-up-right" :size="14" />
      </WdsButton>
    </template>
  </PageHeader>
  <WdsStack v-if="hasClients" direction-column gap="4">
    <WdsStack
      v-for="client in filteredClients"
      :key="client.id"
      class="tw-border tw-border-gray-200 tw-rounded-md tw-bg-white tw-w-full tw-mb-3"
      direction-column
      gap-sm
    >
      <WdsStack justify-space-between align-center class="tw-p-6 tw-pb-3">
        <WdsStack align-center gap-sm>
          <WdsAvatar :path="client.company.avatar_path" />
          <WdsStack direction-column gap-sm class="tw-ms-2">
            <WdsStack align-center>
              <WdsLabel
                :subtle="isStatus(client.status, ContactStatus.Active)"
                :warning="isStatus(client.status, ContactStatus.Invited)"
                :success="isStatus(client.status, ContactStatus.Active)"
              >
                •
                {{
                  isStatus(client.status, ContactStatus.Active)
                    ? $t('Active profile')
                    : $t('Pending invite')
                }}
              </WdsLabel>
              <WdsText secondary class="tw-ml-3">
                {{ $t('Invited on') }} {{ formatDate(client.created_at) }}
              </WdsText>
            </WdsStack>
            <WdsHeading type="title2">{{ client.company.company }}</WdsHeading>
          </WdsStack>
        </WdsStack>

        <WdsStack
          v-if="isStatus(client.status, ContactStatus.Invited)"
          align-center
          class="tw-gap-5"
        >
          <WdsButton
            primary
            :href="laravelNamedRoute('contacts.join', { id: client.id })"
          >
            {{ $t('Accept invitation') }}
          </WdsButton>
        </WdsStack>
      </WdsStack>

      <WdsStack
        v-if="isStatus(client.status, ContactStatus.Invited) && client.message"
        direction-column
        class="tw-bg-true-gray-50 tw-rounded-md tw-p-4 tw-mx-4 tw-mb-3"
      >
        <WdsText>
          <strong>{{ $t('Attached message:') }}</strong>
          {{ client.message }}
        </WdsText>
      </WdsStack>

      <template v-if="hasUnacceptedDocuments(client)">
        <WdsStack
          justify-space-between
          align-center
          class="tw-bg-orange-50 tw-border tw-border-orange-200 tw-border-l-0 tw-border-r-0 tw-py-2 tw-px-4 tw-w-full"
          gap-sm
        >
          <WdsStack align-center gap-sm>
            <WdsIcon :name="WdsIconNameEnum.WARNING" :size="20" class="tw-text-orange-500" />
            <WdsText>
              <span class="tw-text-orange-500">
                {{ $t('Provide missing information to access jobs and offers from this client.') }}
              </span>
            </WdsText>
          </WdsStack>
        </WdsStack>
        <WdsText class="tw-px-6 tw-pt-2">
          {{
            $t(
              ':company requires additional information from all of their trusted contacts before you are able to see their available jobs or accept hire offers:',
              { company: client.company.company },
            )
          }}
        </WdsText>
      </template>
      <WdsStack
        v-if="client.company.settings.require_onboarding && client.onboarding_documents && client.onboarding_documents.length > 0"
        class="tw-rounded-md tw-p-6 tw-pt-0"
        direction-column
      >
        <WdsStack direction-column gap-sm class="tw-mt-2">
          <WdsText>
            Required documents:
            <span class="tw-text-indigo-500 tw-font-bold">*</span>
          </WdsText>

          <WdsStack
            v-for="document in client.onboarding_documents"
            :key="document.id"
            class="tw-border tw-border-gray-200 tw-rounded-md tw-p-4 tw-bg-white"
            justify-space-between
            align-center
            gap-sm
          >
            <WdsStack align-center gap-sm>
              <WdsIcon :name="WdsIconNameEnum.DOCUMENT" :size="20" />
              <WdsText>
                {{ document.file.title ? document.file.title : document.file.name }}
              </WdsText>
            </WdsStack>
            <WdsStack gap-sm align-center>
              <WdsText v-if="document.freelancer_accepted_at !== null">
                <span class="tw-text-green-500">
                  {{ $t('Accepted:') }} {{ formatDate(document.freelancer_accepted_at) }}
                </span>
              </WdsText>
              <WdsButton
                :secondary="document.freelancer_accepted_at === null"
                @click="
                  document.freelancer_accepted_at === null
                    ? openAcceptDocModal(document)
                    : previewDocument(document)
                "
              >
                {{
                  document.freelancer_accepted_at !== null ? $t('Preview') : $t('Review & accept')
                }}
              </WdsButton>
            </WdsStack>
          </WdsStack>
        </WdsStack>
      </WdsStack>
    </WdsStack>
  </WdsStack>
  <WdsStack v-else>
    <WdsStack
      direction-column
      gap-sm
      align-center
      justify-center
      class="tw-bg-true-gray-50 tw-rounded-md tw-p-6 tw-w-full"
    >
      <WdsHeading type="title2">
        {{ $t('You have no clients') }}
      </WdsHeading>
      <WdsText>
        {{ $t('To get started, you need to be invited by a company.') }}
      </WdsText>
    </WdsStack>
  </WdsStack>

  <AcceptContractDocModal
    v-if="selectedDocument"
    v-model="showAcceptDocModal"
    :contract-doc="selectedDocument"
    @update:model-value="handleModalClose"
  />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import {
  WdsAvatar,
  WdsButton,
  WdsIcon,
  WdsText,
  WdsStack,
  WdsHeading,
  WdsLabel,
  WdsIconNameEnum,
} from '@worksome/wds-components'
import PageHeader from './PageHeader.vue'
import AcceptContractDocModal from './compliances/modals/AcceptContractDocModal.vue'
import { $t } from '@/composables/translation'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { ContactStatus } from '@/gql/graphql'

interface Company {
  id: number
  company: string
  avatar_path: string
  settings: {
    require_onboarding: boolean
  }
}

interface File {
  title?: string
  name: string
}

interface OnboardingDocument {
  id: number
  freelancer_accepted_at: string | null
  pdf_path: string
  file: File
  localId?: number
  pdfUrl?: string
}

interface Client {
  id: number
  status: string
  message?: string
  company: Company
  onboarding_documents?: OnboardingDocument[]
  created_at: string
  updated_at: string
}

const props = defineProps<{
  clients: Client[]
}>()

const clients = computed(() => props.clients)

const showAcceptDocModal = ref(false)
const selectedDocument = ref<OnboardingDocument | null>(null)

const isStatus = (clientStatus: string | undefined, compareStatus: string): boolean => {
  if (!clientStatus) return false
  return clientStatus.toUpperCase() === compareStatus
}

const shouldDisplay = (status: string | undefined) => {
  if (!status) return false
  return isStatus(status, ContactStatus.Active) || isStatus(status, ContactStatus.Invited)
}

const filteredClients = computed(() =>
  clients.value.filter((client) => shouldDisplay(client.status)),
)

const hasClients = computed(() => filteredClients.value.length > 0)

const hasUnacceptedDocuments = (client: Client): boolean => {
  if (!client.onboarding_documents || client.onboarding_documents.length === 0) return false
  return client.onboarding_documents.some((doc) => doc.freelancer_accepted_at === null)
}

const formatDate = (dateString: string): string => {
  if (!dateString) return ''

  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

const openAcceptDocModal = (document: OnboardingDocument) => {
  selectedDocument.value = {
    ...document,
    localId: document.id,
    pdfUrl: document.pdf_path,
  }
  showAcceptDocModal.value = true
}

const previewDocument = (document: OnboardingDocument) => {
  if (document.pdf_path) {
    window.open(document.pdf_path, '_blank')
  }
}

const handleModalClose = () => {
  selectedDocument.value = null
  showAcceptDocModal.value = false
}
</script>
